import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiUsers,
  FiUser,
  FiPhone,
  FiMail,
  FiMapPin,
  FiEdit3,
  FiUserPlus,
  FiX,
  FiBarChart2,
  FiFilter,
  FiCalendar,
  FiTrendingUp,
  FiCheckCircle,
  FiAlertTriangle,
  FiDownload,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  BarController,
  LineController,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { cargoService, colaboradorService, irmandadeMembroCargoService } from '../../services/api';
import api from '../../services/api';
import {
  exportarCargoPresencaPNG,
  exportarCargoPresencaJPEG,
  exportarCargoPresencaPDF,
  exportarElementoComoPNG,
  exportarElementoComoJPEG,
  exportarElementoComoPDFImagem,
  exportarCargoPresencaTabelaPDF,
  sanitizarNomeArquivo,
} from '../../utils/exportHelpers';
import './Cargos.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  BarController,
  LineController,
  Title,
  Tooltip,
  Legend
);

/** Rótulos com % acima de cada barra, na mesma cor do preenchimento. */
const cargoPresencaBarTopLabelsPlugin = {
  id: 'cargoPresencaBarTopLabels',
  afterDatasetsDraw(chart) {
    const meta = chart.getDatasetMeta(0);
    if (!meta || meta.hidden) return;

    const dataset = chart.data.datasets[0];
    const { ctx } = chart;

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.font = '600 12px system-ui, -apple-system, "Segoe UI", sans-serif';

    meta.data.forEach((bar, i) => {
      if (!bar || bar.skip) return;
      const raw = dataset.data[i];
      const pct = typeof raw === 'number' ? raw : Number(raw?.y ?? raw ?? 0);
      const bg = Array.isArray(dataset.backgroundColor)
        ? dataset.backgroundColor[i]
        : dataset.backgroundColor;
      ctx.fillStyle = typeof bg === 'string' ? bg : '#334155';
      const pctInt = Math.round(pct);
      const label = `${pctInt}%`;
      ctx.fillText(label, bar.x, bar.y - 4);
    });

    ctx.restore();
  },
};

const catLabel = (c) => {
  if (c === 'crianca') return 'Criança';
  if (c === 'jovem') return 'Jovem';
  if (c === 'adulto') return 'Adulto';
  return c || '—';
};

const nomeColab = (c) => c.nomeCompleto || c.nome_completo || '';

const nomeMembro = (m) => m.nome_completo || m.nomeCompleto || '';

/** Alinhado ao backend: cargos "Auxiliar de Jovens" — só criança ou jovem */
function cargoRequerSomenteCriancaOuJovem(nomeCargo) {
  if (!nomeCargo || typeof nomeCargo !== 'string') return false;
  const n = nomeCargo
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return n.includes('auxiliar') && n.includes('jov');
}

const DetalhesCargo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const cargoIdNum = parseInt(id, 10);

  const [loading, setLoading] = useState(true);
  const [cargo, setCargo] = useState(null);
  const [colaboradores, setColaboradores] = useState([]);
  const [membros, setMembros] = useState([]);

  const [modalVincular, setModalVincular] = useState(false);
  const [todosColaboradores, setTodosColaboradores] = useState([]);
  const [loadingListaColab, setLoadingListaColab] = useState(false);
  const [buscaModal, setBuscaModal] = useState('');
  const [vinculandoId, setVinculandoId] = useState(null);
  const [desvinculandoId, setDesvinculandoId] = useState(null);

  const [modalVincularMembro, setModalVincularMembro] = useState(false);
  const [todosMembros, setTodosMembros] = useState([]);
  const [loadingListaMembros, setLoadingListaMembros] = useState(false);
  const [buscaModalMembro, setBuscaModalMembro] = useState('');
  const [vinculandoMembroId, setVinculandoMembroId] = useState(null);
  const [desvinculandoMembroId, setDesvinculandoMembroId] = useState(null);

  // Gráfico de presença
  const anoAtual = new Date().getFullYear();
  const [statsPresenca, setStatsPresenca] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [filtroAno, setFiltroAno] = useState(String(anoAtual));
  const [filtroTipo, setFiltroTipo] = useState('ano'); // 'ano' | 'periodo'
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [periodoPresencaApi, setPeriodoPresencaApi] = useState({ inicio: null, fim: null });
  const kpiPresencaExportRef = useRef(null);
  const graficoPresencaExportRef = useRef(null);
  const graficoSoloExportRef = useRef(null);
  const tabelaSoloExportRef = useRef(null);

  const restringeCriancaJovem = useMemo(
    () => (cargo ? cargoRequerSomenteCriancaOuJovem(cargo.nome) : false),
    [cargo]
  );

  const carregarStatsPresenca = useCallback(async () => {
    if (!id || Number.isNaN(cargoIdNum)) return;
    setLoadingStats(true);
    try {
      let url = `/cargos/${id}/presenca-stats`;
      if (filtroTipo === 'periodo' && filtroDataInicio && filtroDataFim) {
        url += `?data_inicio=${filtroDataInicio}&data_fim=${filtroDataFim}`;
      } else {
        url += `?ano=${filtroAno}`;
      }
      const res = await api.get(url);
      const dados = res.data?.data || [];
      setStatsPresenca(dados);
      setPeriodoPresencaApi({
        inicio: res.data?.dataInicio || null,
        fim: res.data?.dataFim || null,
      });
    } catch (e) {
      console.error('Erro ao carregar stats de presença:', e);
      toast.error('Erro ao carregar estatísticas de presença');
    } finally {
      setLoadingStats(false);
    }
  }, [id, cargoIdNum, filtroAno, filtroTipo, filtroDataInicio, filtroDataFim]);

  const carregarVinculos = useCallback(async () => {
    if (!id || Number.isNaN(cargoIdNum)) {
      toast.error('Cargo inválido');
      navigate('/cargos');
      return;
    }
    const res = await cargoService.getVinculos(id);
    if (res.success && res.data) {
      setCargo(res.data.cargo);
      setColaboradores(res.data.colaboradores || []);
      setMembros(res.data.membros_irmandade || []);
    } else {
      throw new Error('Falha ao carregar');
    }
  }, [id, cargoIdNum, navigate]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        await carregarVinculos();
      } catch (e) {
        if (!cancel) {
          console.error(e);
          toast.error('Cargo não encontrado ou erro ao carregar');
          navigate('/cargos');
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [carregarVinculos, navigate]);

  useEffect(() => {
    carregarStatsPresenca();
  }, [carregarStatsPresenca]);

  const abrirModalVincular = async () => {
    setModalVincular(true);
    setModalVincularMembro(false);
    setBuscaModal('');
    setLoadingListaColab(true);
    try {
      const res = await colaboradorService.getAll();
      const lista = Array.isArray(res.data) ? res.data : [];
      setTodosColaboradores(lista);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar colaboradores');
      setTodosColaboradores([]);
    } finally {
      setLoadingListaColab(false);
    }
  };

  const abrirModalVincularMembro = async () => {
    setModalVincularMembro(true);
    setModalVincular(false);
    setBuscaModalMembro('');
    setLoadingListaMembros(true);
    try {
      const res = await irmandadeMembroCargoService.listarTodosMembros();
      const lista = Array.isArray(res.data) ? res.data : [];
      setTodosMembros(lista);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar membros');
      setTodosMembros([]);
    } finally {
      setLoadingListaMembros(false);
    }
  };

  const temCargoColab = (c) => {
    const ids = c.cargo_ids || [];
    const principal = c.cargoId ?? c.cargo_id;
    return ids.includes(cargoIdNum) || principal === cargoIdNum;
  };

  const idsMembrosVinculados = useMemo(() => new Set(membros.map((m) => m.id)), [membros]);

  const colaboradoresParaVincular = todosColaboradores.filter((c) => {
    if (temCargoColab(c)) return false;
    const n = nomeColab(c).toLowerCase();
    const q = buscaModal.trim().toLowerCase();
    if (!q) return true;
    return n.includes(q);
  });

  const membrosParaVincular = todosMembros.filter((m) => {
    if (idsMembrosVinculados.has(m.id)) return false;
    if (restringeCriancaJovem && m.categoria !== 'crianca' && m.categoria !== 'jovem') {
      return false;
    }
    const n = nomeMembro(m).toLowerCase();
    const q = buscaModalMembro.trim().toLowerCase();
    if (!q) return true;
    return n.includes(q);
  });

  const vincular = async (colaboradorId) => {
    try {
      setVinculandoId(colaboradorId);
      await colaboradorService.addCargos(colaboradorId, [cargoIdNum]);
      toast.success('Colaborador vinculado ao cargo.');
      await carregarVinculos();
      setTodosColaboradores((prev) =>
        prev.map((c) =>
          c.id === colaboradorId
            ? { ...c, cargo_ids: [...new Set([...(c.cargo_ids || []), cargoIdNum])] }
            : c
        )
      );
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao vincular');
    } finally {
      setVinculandoId(null);
    }
  };

  const desvincular = async (colaboradorId) => {
    if (!window.confirm('Remover este colaborador deste cargo?')) return;
    try {
      setDesvinculandoId(colaboradorId);
      await colaboradorService.removeCargo(colaboradorId, cargoIdNum);
      toast.success('Colaborador desvinculado.');
      await carregarVinculos();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao desvincular');
    } finally {
      setDesvinculandoId(null);
    }
  };

  const vincularMembro = async (membroId) => {
    try {
      setVinculandoMembroId(membroId);
      await irmandadeMembroCargoService.adicionarCargosAoMembro(membroId, [cargoIdNum]);
      toast.success('Membro vinculado ao cargo.');
      await carregarVinculos();
      setTodosMembros((prev) => prev.filter((m) => m.id !== membroId));
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Erro ao vincular');
    } finally {
      setVinculandoMembroId(null);
    }
  };

  const desvincularMembro = async (membroId) => {
    if (!window.confirm('Remover este membro deste cargo?')) return;
    try {
      setDesvinculandoMembroId(membroId);
      await irmandadeMembroCargoService.removerCargoDoMembro(membroId, cargoIdNum);
      toast.success('Membro desvinculado.');
      await carregarVinculos();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao desvincular');
    } finally {
      setDesvinculandoMembroId(null);
    }
  };

  const periodoPresencaLabel = useMemo(() => {
    if (periodoPresencaApi.inicio && periodoPresencaApi.fim) {
      const fmt = (iso) => {
        const [y, m, d] = iso.split('T')[0].split('-');
        return `${d}/${m}/${y}`;
      };
      return `${fmt(periodoPresencaApi.inicio)} – ${fmt(periodoPresencaApi.fim)}`;
    }
    if (filtroTipo === 'ano') return `Ano calendário ${filtroAno}`;
    return 'Período personalizado';
  }, [periodoPresencaApi, filtroTipo, filtroAno]);

  const executarExportacaoPresenca = useCallback(
    async (modo) => {
      if (!modo) return;
      if (!statsPresenca.length) {
        toast.error('Nada para exportar.');
        return;
      }
      const base = sanitizarNomeArquivo(
        `${cargo?.nome || 'cargo'}_presenca_${filtroTipo === 'ano' ? filtroAno : 'periodo'}_${Date.now()}`
      );
      const tabelaLinhas = statsPresenca.map((s) => [
        s.nome,
        String(s.totalReunioes),
        String(s.presentes),
        String(s.ausentes),
        s.totalReunioes === 0 ? '—' : `${s.percentual ?? 0}%`,
      ]);
      const tituloDoc = `Frequência de presenças — ${cargo?.nome || 'Cargo'}`;

      try {
        toast.info('Gerando arquivo…');
        switch (modo) {
          case 'full-png':
            await exportarCargoPresencaPNG(kpiPresencaExportRef.current, graficoPresencaExportRef.current, `${base}_completo`);
            break;
          case 'full-jpeg':
            await exportarCargoPresencaJPEG(kpiPresencaExportRef.current, graficoPresencaExportRef.current, `${base}_completo`);
            break;
          case 'full-pdf':
            await exportarCargoPresencaPDF(kpiPresencaExportRef.current, graficoPresencaExportRef.current, `${base}_completo`, {
              tituloCargo: tituloDoc,
              periodoLabel: periodoPresencaLabel,
              tabelaLinhas,
            });
            break;
          case 'chart-png':
            await exportarElementoComoPNG(graficoSoloExportRef.current, `${base}_grafico`);
            break;
          case 'chart-jpeg':
            await exportarElementoComoJPEG(graficoSoloExportRef.current, `${base}_grafico`);
            break;
          case 'chart-pdf':
            await exportarElementoComoPDFImagem(graficoSoloExportRef.current, `${base}_grafico`);
            break;
          case 'table-pdf':
            await exportarCargoPresencaTabelaPDF(tabelaLinhas, `${base}_tabela`, {
              tituloCargo: tituloDoc,
              periodoLabel: periodoPresencaLabel,
            });
            break;
          case 'table-png':
            await exportarElementoComoPNG(tabelaSoloExportRef.current, `${base}_tabela`);
            break;
          default:
            return;
        }
        toast.success('Download iniciado.');
      } catch (err) {
        console.error(err);
        toast.error('Não foi possível gerar o arquivo.');
      }
    },
    [cargo?.nome, filtroAno, filtroTipo, periodoPresencaLabel, statsPresenca]
  );

  const kpiPresencaCargo = useMemo(() => {
    const list = statsPresenca || [];
    if (list.length === 0) {
      return {
        totalPessoas: 0,
        media: null,
        acimaMeta: 0,
        faixa50a69: 0,
        abaixo50: 0,
        somaPresentes: 0,
        somaAusentes: 0,
        comFrequencia: 0,
        taxaEquipe: null,
      };
    }
    let somaPct = 0;
    let nPct = 0;
    let acima = 0;
    let faixa = 0;
    let abaixo = 0;
    let sp = 0;
    let sa = 0;
    for (const s of list) {
      sp += s.presentes ?? 0;
      sa += s.ausentes ?? 0;
      const tr = s.totalReunioes ?? 0;
      const p = s.percentual;
      if (tr > 0 && p != null) {
        somaPct += p;
        nPct++;
        if (p >= 70) acima++;
        else if (p >= 50) faixa++;
        else abaixo++;
      }
    }
    const totalReg = sp + sa;
    return {
      totalPessoas: list.length,
      media: nPct > 0 ? Math.round(somaPct / nPct) : null,
      acimaMeta: acima,
      faixa50a69: faixa,
      abaixo50: abaixo,
      somaPresentes: sp,
      somaAusentes: sa,
      comFrequencia: nPct,
      taxaEquipe: totalReg > 0 ? Math.round((sp / totalReg) * 1000) / 10 : null,
    };
  }, [statsPresenca]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <p>Carregando cargo…</p>
        </div>
      </div>
    );
  }

  if (!cargo) return null;

  const created = cargo.createdAt ? new Date(cargo.createdAt).toLocaleDateString('pt-BR') : '—';

  return (
    <div className="cargos-page cargo-detalhes-page">
      <header className="page-header cargo-detalhes-header">
        <div className="cargo-detalhes-topbar">
          <button type="button" className="cargo-detalhes-voltar" onClick={() => navigate('/cargos')}>
            <FiArrowLeft /> Voltar
          </button>
        </div>

        <div className="cargo-detalhes-header-grid">
          <div className="cargo-detalhes-main">
            <h1 className="cargo-detalhes-titulo">
              <FiUsers className="page-icon" aria-hidden />
              <span className="cargo-detalhes-titulo-texto">{cargo.nome}</span>
            </h1>

            <div className="cargo-detalhes-descricao-wrap">
              <p className="cargo-detalhes-descricao">{cargo.descricao || 'Sem descrição'}</p>
            </div>

            <div className="cargo-detalhes-meta">
              <span className={`status-badge ${cargo.ativo ? 'active' : 'inactive'}`}>
                {cargo.ativo ? 'Ativo' : 'Inativo'}
              </span>
              <span className="cargo-detalhes-criado">Criado em {created}</span>
            </div>

            {restringeCriancaJovem && (
              <p className="cargo-detalhes-aviso">
                Este cargo aceita apenas membros da categoria <strong>Criança</strong> ou <strong>Jovem</strong>{' '}
                (regra para cargos tipo Auxiliar de Jovens).
              </p>
            )}
          </div>

          <div className="cargo-detalhes-header-actions">
            <button type="button" className="btn-primary cargo-detalhes-btn-editar" onClick={() => navigate(`/cargos/editar/${id}`)}>
              <FiEdit3 /> Editar cargo
            </button>
          </div>
        </div>
      </header>

      <div className="cargo-detalhes-stats">
        <div className="stat-card">
          <span className="stat-number">{colaboradores.length}</span>
          <span className="stat-label">Colaborador(es)</span>
        </div>
        <div className="stat-card active">
          <span className="stat-number">{membros.length}</span>
          <span className="stat-label">Membro(s) da irmandade</span>
        </div>
      </div>

      <p className="cargo-presenca-anchor-hint">
        <a href="#cargo-frequencia-presenca">
          Frequência de presenças, gráfico, tabela e exportação
        </a>
      </p>

      <section
        id="cargo-frequencia-presenca"
        className="cargo-detalhes-secao cargo-presenca-secao"
        aria-labelledby="cargo-frequencia-presenca-titulo"
      >
        <div className="cargo-detalhes-secao-header cargo-presenca-header">
          <div className="cargo-presenca-header-titulo">
            <h2 id="cargo-frequencia-presenca-titulo">
              <FiBarChart2 /> Frequência de Presenças nas Reuniões
            </h2>
            <p className="cargo-presenca-periodo-label">
              <FiCalendar size={15} aria-hidden />
              <span>{periodoPresencaLabel}</span>
            </p>
          </div>
          {!loadingStats && statsPresenca.length > 0 && (
            <div className="cargo-presenca-export-toolbar cargo-presenca-export-toolbar--header">
              <label htmlFor="cargo-presenca-export-select" className="cargo-presenca-export-label">
                <FiDownload size={16} aria-hidden /> Exportar
              </label>
              <select
                id="cargo-presenca-export-select"
                className="cargo-presenca-export-select"
                defaultValue=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) {
                    executarExportacaoPresenca(v);
                    e.target.value = '';
                  }
                }}
                aria-label="Escolher formato de exportação"
              >
                <option value="">Formato…</option>
                <optgroup label="Indicadores completos (KPI + gráfico)">
                  <option value="full-png">PNG</option>
                  <option value="full-jpeg">JPEG</option>
                  <option value="full-pdf">PDF (inclui tabela no final)</option>
                </optgroup>
                <optgroup label="Somente gráfico (títulos e legenda)">
                  <option value="chart-png">PNG</option>
                  <option value="chart-jpeg">JPEG</option>
                  <option value="chart-pdf">PDF</option>
                </optgroup>
                <optgroup label="Somente tabela">
                  <option value="table-pdf">PDF</option>
                  <option value="table-png">PNG</option>
                </optgroup>
              </select>
            </div>
          )}
        </div>

        {!loadingStats && statsPresenca.length > 0 && (
          <div ref={kpiPresencaExportRef} className="cargo-presenca-kpis" role="region" aria-label="Indicadores de frequência do cargo">
            <div className="cargo-presenca-kpi">
              <span className="cargo-presenca-kpi-label">Colaboradores analisados</span>
              <span className="cargo-presenca-kpi-value">{kpiPresencaCargo.totalPessoas}</span>
            </div>
            <div className="cargo-presenca-kpi cargo-presenca-kpi--destaque">
              <span className="cargo-presenca-kpi-label">
                <FiTrendingUp size={14} aria-hidden /> Média de frequência
              </span>
              <span className="cargo-presenca-kpi-value">
                {kpiPresencaCargo.media != null ? `${kpiPresencaCargo.media}%` : '—'}
              </span>
            </div>
            <div className="cargo-presenca-kpi">
              <span className="cargo-presenca-kpi-label">Taxa geral (presenças)</span>
              <span className="cargo-presenca-kpi-value">
                {kpiPresencaCargo.taxaEquipe != null ? `${kpiPresencaCargo.taxaEquipe}%` : '—'}
              </span>
              <span className="cargo-presenca-kpi-hint">
                {kpiPresencaCargo.somaPresentes} pres. / {kpiPresencaCargo.somaPresentes + kpiPresencaCargo.somaAusentes} ocorr.
              </span>
            </div>
            <div className="cargo-presenca-kpi cargo-presenca-kpi--ok">
              <span className="cargo-presenca-kpi-label">
                <FiCheckCircle size={14} aria-hidden /> ≥ meta 70%
              </span>
              <span className="cargo-presenca-kpi-value">{kpiPresencaCargo.acimaMeta}</span>
              <span className="cargo-presenca-kpi-hint">pessoas</span>
            </div>
            <div className="cargo-presenca-kpi cargo-presenca-kpi--medio">
              <span className="cargo-presenca-kpi-label">50% – 69%</span>
              <span className="cargo-presenca-kpi-value">{kpiPresencaCargo.faixa50a69}</span>
              <span className="cargo-presenca-kpi-hint">pessoas</span>
            </div>
            <div className="cargo-presenca-kpi cargo-presenca-kpi--baixo">
              <span className="cargo-presenca-kpi-label">
                <FiAlertTriangle size={14} aria-hidden /> 0% – 49%
              </span>
              <span className="cargo-presenca-kpi-value">{kpiPresencaCargo.abaixo50}</span>
              <span className="cargo-presenca-kpi-hint">pessoas</span>
            </div>
          </div>
        )}

        <div className="cargo-presenca-filtros">
          <div className="cargo-presenca-filtro-tipo">
            <button
              type="button"
              className={`cargo-presenca-tab${filtroTipo === 'ano' ? ' active' : ''}`}
              onClick={() => setFiltroTipo('ano')}
            >
              Por Ano
            </button>
            <button
              type="button"
              className={`cargo-presenca-tab${filtroTipo === 'periodo' ? ' active' : ''}`}
              onClick={() => setFiltroTipo('periodo')}
            >
              Período Personalizado
            </button>
          </div>

          {filtroTipo === 'ano' ? (
            <div className="cargo-presenca-filtro-row">
              <FiFilter size={14} />
              <select
                value={filtroAno}
                onChange={(e) => setFiltroAno(e.target.value)}
                className="cargo-presenca-select"
              >
                {Array.from({ length: 6 }, (_, i) => anoAtual - i).map((a) => (
                  <option key={a} value={String(a)}>{a}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="cargo-presenca-filtro-row">
              <input
                type="date"
                value={filtroDataInicio}
                onChange={(e) => setFiltroDataInicio(e.target.value)}
                className="cargo-presenca-input"
              />
              <span>até</span>
              <input
                type="date"
                value={filtroDataFim}
                onChange={(e) => setFiltroDataFim(e.target.value)}
                className="cargo-presenca-input"
              />
              <button
                type="button"
                className="cargo-btn-vincular"
                onClick={carregarStatsPresenca}
                disabled={!filtroDataInicio || !filtroDataFim}
                style={{ padding: '6px 14px' }}
              >
                Aplicar
              </button>
            </div>
          )}
        </div>

        {loadingStats ? (
          <p className="cargo-detalhes-vazio">Carregando estatísticas…</p>
        ) : statsPresenca.length === 0 ? (
          <p className="cargo-detalhes-vazio">
            Nenhum colaborador vinculado ou sem dados de presença para o período selecionado.
          </p>
        ) : (
          <>
            <div ref={graficoSoloExportRef} className="cargo-presenca-solo-grafico-bloco">
              <div className="cargo-presenca-solo-topo">
                <span className="cargo-presenca-solo-topo-cargo">{cargo.nome}</span>
                <span className="cargo-presenca-solo-topo-sep" aria-hidden>
                  {' '}
                  ·{' '}
                </span>
                <span className="cargo-presenca-solo-topo-periodo">{periodoPresencaLabel}</span>
              </div>
              <h3 className="cargo-presenca-subtitulo" id="cargo-presenca-grafico-titulo">
                Gráfico por colaborador
              </h3>
              <div ref={graficoPresencaExportRef} className="cargo-presenca-grafico-export">
                <div className="cargo-presenca-legenda">
                  <span className="cargo-presenca-legenda-item" style={{ color: '#ef4444' }}>■ 0–49%</span>
                  <span className="cargo-presenca-legenda-item" style={{ color: '#eab308' }}>■ 50–69%</span>
                  <span className="cargo-presenca-legenda-item" style={{ color: '#22c55e' }}>■ ≥70%</span>
                  <span className="cargo-presenca-legenda-item" style={{ color: '#3b82f6' }}>— Meta 70%</span>
                </div>
                <div className="cargo-presenca-chart-wrap">
              <Chart
                plugins={[cargoPresencaBarTopLabelsPlugin]}
                type="bar"
                data={{
                  labels: statsPresenca.map((s) => s.nome.split(' ')[0] + (s.nome.split(' ').length > 1 ? ' ' + s.nome.split(' ').slice(-1)[0] : '')),
                  datasets: [
                    {
                      type: 'bar',
                      label: 'Presença (%)',
                      data: statsPresenca.map((s) => s.percentual ?? 0),
                      backgroundColor: statsPresenca.map((s) => {
                        const p = s.percentual ?? 0;
                        if (p >= 70) return '#22c55e';
                        if (p >= 50) return '#eab308';
                        return '#ef4444';
                      }),
                      borderRadius: 4,
                      borderSkipped: false,
                    },
                    {
                      type: 'line',
                      label: 'Meta (70%)',
                      data: statsPresenca.map(() => 70),
                      borderColor: '#3b82f6',
                      borderWidth: 2,
                      borderDash: [6, 4],
                      pointRadius: 0,
                      fill: false,
                      tension: 0,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  layout: {
                    padding: { top: 20 },
                  },
                  plugins: {
                    legend: { position: 'top' },
                    datalabels: { display: false },
                    rjDatalabels: { display: false },
                    tooltip: {
                      callbacks: {
                        label: (ctx) => {
                          if (ctx.dataset.type === 'line') return `Meta: 70%`;
                          const s = statsPresenca[ctx.dataIndex];
                          if (!s) return '';
                          return [
                            `Presença: ${s.percentual ?? 0}%`,
                            `Presentes: ${s.presentes} / ${s.totalReunioes} reuniões`,
                          ];
                        },
                      },
                    },
                  },
                  scales: {
                    y: {
                      min: 0,
                      max: 100,
                      ticks: { callback: (v) => `${v}%` },
                      grid: { color: 'rgba(0,0,0,0.06)' },
                    },
                    x: {
                      grid: { display: false },
                    },
                  },
                }}
                style={{ height: 340 }}
              />
                </div>
              </div>
            </div>

            <div ref={tabelaSoloExportRef} className="cargo-presenca-solo-tabela-bloco">
              <div className="cargo-presenca-solo-topo">
                <span className="cargo-presenca-solo-topo-cargo">{cargo.nome}</span>
                <span className="cargo-presenca-solo-topo-sep" aria-hidden>
                  {' '}
                  ·{' '}
                </span>
                <span className="cargo-presenca-solo-topo-periodo">{periodoPresencaLabel}</span>
              </div>
              <h3 className="cargo-presenca-subtitulo cargo-presenca-subtitulo-tabela" id="cargo-presenca-tabela-titulo">
                Tabela — detalhamento por colaborador
              </h3>
              <div className="cargo-presenca-tabela-wrap">
              <table className="cargo-presenca-tabela" aria-labelledby="cargo-presenca-tabela-titulo">
                <thead>
                  <tr>
                    <th>Colaborador</th>
                    <th>Reuniões</th>
                    <th>Presentes</th>
                    <th>Ausentes</th>
                    <th>Frequência</th>
                  </tr>
                </thead>
                <tbody>
                  {statsPresenca.map((s) => {
                    const p = s.percentual ?? 0;
                    const cor = p >= 70 ? '#22c55e' : p >= 50 ? '#eab308' : '#ef4444';
                    return (
                      <tr key={s.colaboradorId}>
                        <td>{s.nome}</td>
                        <td>{s.totalReunioes}</td>
                        <td>{s.presentes}</td>
                        <td>{s.ausentes}</td>
                        <td>
                          <span style={{ fontWeight: 700, color: cor }}>
                            {s.totalReunioes === 0 ? '—' : `${p}%`}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          </>
        )}
      </section>

      <section className="cargo-detalhes-secao">
        <div className="cargo-detalhes-secao-header">
          <h2><FiUser /> Colaboradores com este cargo</h2>
          <button type="button" className="cargo-btn-vincular" onClick={abrirModalVincular}>
            <FiUserPlus size={18} /> Vincular colaborador
          </button>
        </div>
        {colaboradores.length === 0 ? (
          <p className="cargo-detalhes-vazio">Nenhum colaborador vinculado. Use o botão acima para adicionar.</p>
        ) : (
          <ul className="cargo-detalhes-lista">
            {colaboradores.map((c) => (
              <li key={c.id} className="cargo-detalhes-item">
                <div className="cargo-detalhes-nome">{c.nome}</div>
                <div className="cargo-detalhes-dados">
                  {c.tipo_pessoa && <span className="cargo-detalhes-tag">{c.tipo_pessoa}</span>}
                  {c.congregacao && (
                    <span><FiMapPin size={14} /> {c.congregacao}</span>
                  )}
                  {c.telefone && (
                    <span><FiPhone size={14} /> {c.telefone}</span>
                  )}
                  {c.email && (
                    <span><FiMail size={14} /> {c.email}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="cargo-btn-desvincular"
                    disabled={desvinculandoId === c.id}
                    onClick={() => desvincular(c.id)}
                  >
                    {desvinculandoId === c.id ? '…' : 'Desvincular'}
                  </button>
                  <button type="button" className="btn-link-cargo" onClick={() => navigate(`/colaboradores/editar/${c.id}`)}>
                    Ver colaborador
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="cargo-detalhes-secao">
        <div className="cargo-detalhes-secao-header">
          <h2><FiUsers /> Membros da irmandade com este cargo</h2>
          <button type="button" className="cargo-btn-vincular" onClick={abrirModalVincularMembro}>
            <FiUserPlus size={18} /> Vincular membro
          </button>
        </div>
        {membros.length === 0 ? (
          <p className="cargo-detalhes-vazio">
            Nenhum membro vinculado. Use &quot;Vincular membro&quot; para associar qualquer membro
            {restringeCriancaJovem ? ' (apenas crianças ou jovens neste cargo).' : '.'}
          </p>
        ) : (
          <ul className="cargo-detalhes-lista">
            {membros.map((m) => (
              <li key={m.id} className="cargo-detalhes-item">
                <div className="cargo-detalhes-nome">{m.nome}</div>
                <div className="cargo-detalhes-dados">
                  <span className="cargo-detalhes-tag">{catLabel(m.categoria)}</span>
                  {m.congregacao && (
                    <span><FiMapPin size={14} /> {m.congregacao}</span>
                  )}
                  {m.telefone && (
                    <span><FiPhone size={14} /> {m.telefone}</span>
                  )}
                  {m.email && (
                    <span><FiMail size={14} /> {m.email}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="cargo-btn-desvincular"
                    disabled={desvinculandoMembroId === m.id}
                    onClick={() => desvincularMembro(m.id)}
                  >
                    {desvinculandoMembroId === m.id ? '…' : 'Desvincular'}
                  </button>
                  <button type="button" className="btn-link-cargo" onClick={() => navigate(`/irmandade/editar/${m.id}`)}>
                    Ver membro
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {modalVincular && (
        <div className="cargo-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="cargo-modal-titulo">
          <div className="cargo-modal">
            <div className="cargo-modal-header">
              <h3 id="cargo-modal-titulo">Vincular colaborador a {cargo.nome}</h3>
              <button type="button" className="cargo-modal-close" onClick={() => setModalVincular(false)} aria-label="Fechar">
                <FiX />
              </button>
            </div>
            <div className="cargo-modal-body">
              <input
                type="search"
                className="cargo-modal-busca"
                placeholder="Filtrar por nome..."
                value={buscaModal}
                onChange={(e) => setBuscaModal(e.target.value)}
              />
              {loadingListaColab ? (
                <p className="cargo-modal-vazio">Carregando…</p>
              ) : colaboradoresParaVincular.length === 0 ? (
                <p className="cargo-modal-vazio">
                  {todosColaboradores.length === 0
                    ? 'Não há colaboradores cadastrados.'
                    : 'Todos os colaboradores já possuem este cargo ou nada corresponde à busca.'}
                </p>
              ) : (
                <ul className="cargo-modal-lista">
                  {colaboradoresParaVincular.map((c) => (
                    <li key={c.id} className="cargo-modal-item">
                      <span>{nomeColab(c)}</span>
                      <button
                        type="button"
                        disabled={vinculandoId === c.id}
                        onClick={() => vincular(c.id)}
                      >
                        {vinculandoId === c.id ? '…' : 'Vincular'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {modalVincularMembro && (
        <div className="cargo-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="cargo-modal-membro-titulo">
          <div className="cargo-modal">
            <div className="cargo-modal-header">
              <h3 id="cargo-modal-membro-titulo">Vincular membro a {cargo.nome}</h3>
              <button type="button" className="cargo-modal-close" onClick={() => setModalVincularMembro(false)} aria-label="Fechar">
                <FiX />
              </button>
            </div>
            <div className="cargo-modal-body">
              {restringeCriancaJovem && (
                <p className="cargo-modal-hint">
                  Somente <strong>crianças</strong> e <strong>jovens</strong> aparecem nesta lista.
                </p>
              )}
              <input
                type="search"
                className="cargo-modal-busca"
                placeholder="Filtrar por nome..."
                value={buscaModalMembro}
                onChange={(e) => setBuscaModalMembro(e.target.value)}
              />
              {loadingListaMembros ? (
                <p className="cargo-modal-vazio">Carregando…</p>
              ) : membrosParaVincular.length === 0 ? (
                <p className="cargo-modal-vazio">
                  {todosMembros.length === 0
                    ? 'Não há membros cadastrados.'
                    : restringeCriancaJovem
                      ? 'Nenhum membro criança/jovem disponível (ou todos já têm este cargo).'
                      : 'Todos os membros já possuem este cargo ou nada corresponde à busca.'}
                </p>
              ) : (
                <ul className="cargo-modal-lista">
                  {membrosParaVincular.map((m) => (
                    <li key={m.id} className="cargo-modal-item">
                      <span>
                        {nomeMembro(m)}{' '}
                        <span className="cargo-modal-item-cat">({catLabel(m.categoria)})</span>
                      </span>
                      <button
                        type="button"
                        disabled={vinculandoMembroId === m.id}
                        onClick={() => vincularMembro(m.id)}
                      >
                        {vinculandoMembroId === m.id ? '…' : 'Vincular'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetalhesCargo;
